import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import { exchangeFirebaseTokenForUserInfo } from "../shared/api/auth";
import { updateMyWorkingHours } from "../shared/api/userPreferences";
import {
  useAccountWorkingHours,
  userHasWorkingHoursOverride,
} from "../shared/hooks/useAccountWorkingHours";
import TimeSelect from "./TimeSelect";
import TimezoneSelect from "./TimezoneSelect";
import { timezoneDisplayLabel } from "../shared/utils/timezoneOptions";
import { buildTimeOptionsForWorkingStart } from "../shared/utils/dueDateTime";
import { getMessageForFetchError } from "../shared/utils/apiError";

const timeSelectStyle = {
  padding: "4px 8px",
  fontSize: "13px",
  minWidth: "100px",
  width: "auto",
  flex: "0 0 auto",
  boxSizing: "border-box",
};

const DEFAULT_START = "08:00";
const DEFAULT_END = "17:00";

function normalizeHhmm(raw) {
  if (raw == null || String(raw).trim() === "") return "";
  const t = String(raw).trim();
  const m = /^([01]?\d|2[0-3]):([0-5]\d)$/.exec(t);
  if (!m) return t;
  return `${String(parseInt(m[1], 10)).padStart(2, "0")}:${m[2]}`;
}

function hhmmToMinutes(hhmm) {
  const n = normalizeHhmm(hhmm);
  if (!n || !/^\d{2}:\d{2}$/.test(n)) return null;
  const [h, m] = n.split(":").map((x) => parseInt(x, 10));
  return h * 60 + m;
}

function formatRangePretty(start, end) {
  const s =
    start != null && String(start).trim() !== ""
      ? String(start).trim()
      : DEFAULT_START;
  const e =
    end != null && String(end).trim() !== ""
      ? String(end).trim()
      : DEFAULT_END;
  const fmt = (hhmm) => {
    const n = normalizeHhmm(hhmm);
    if (!n || !/^\d{2}:\d{2}$/.test(n)) return String(hhmm).trim() || "—";
    const [h, m] = n.split(":").map((x) => parseInt(x, 10) || 0);
    const d = new Date(2000, 0, 1, h, m, 0);
    return d.toLocaleTimeString(undefined, {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  };
  return `${fmt(s)} – ${fmt(e)}`;
}

function isOrgDefaultDraft(start, end, tz, orgStart, orgEnd, accountTz) {
  const tzTrim = (tz || "").trim();
  const acc = (accountTz || "").trim();
  return (
    normalizeHhmm(start) === normalizeHhmm(orgStart) &&
    normalizeHhmm(end) === normalizeHhmm(orgEnd) &&
    (!tzTrim || tzTrim === acc)
  );
}

function draftMatchesPersisted(start, end, tz, userInfo, orgStart, orgEnd, accountTz) {
  if (userHasWorkingHoursOverride(userInfo)) {
    const utz =
      userInfo.working_hours_time_zone != null
        ? String(userInfo.working_hours_time_zone).trim()
        : "";
    return (
      normalizeHhmm(start) === normalizeHhmm(userInfo.working_hours_start) &&
      normalizeHhmm(end) === normalizeHhmm(userInfo.working_hours_end) &&
      (tz || "").trim() === utz
    );
  }
  return isOrgDefaultDraft(start, end, tz, orgStart, orgEnd, accountTz);
}

/**
 * Working hours + time zone (per-user override vs organization default).
 * @param {{ fieldIdPrefix?: string }} [props]
 */
function ProfileWorkingHoursPanel({ fieldIdPrefix = "profile-wh" } = {}) {
  const { user, userInfo, setUserInfo } = useAuth();
  const accountWh = useAccountWorkingHours();
  const [editing, setEditing] = useState(false);
  const [start, setStart] = useState(DEFAULT_START);
  const [end, setEnd] = useState(DEFAULT_END);
  const [tz, setTz] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const timeOptions = useMemo(
    () => buildTimeOptionsForWorkingStart("00:00").filter((o) => o.value !== ""),
    []
  );

  /** Latest slot that still allows a later half-hour the same day (end max 23:30). */
  const maxStartMinutes = 23 * 60;

  const startOptions = useMemo(() => {
    const endM = hhmmToMinutes(normalizeHhmm(end) || end);
    return timeOptions.filter((o) => {
      const m = hhmmToMinutes(o.value);
      if (m == null) return false;
      if (m > maxStartMinutes) return false;
      if (endM != null && m >= endM) return false;
      return true;
    });
  }, [timeOptions, end, maxStartMinutes]);

  const endOptions = useMemo(() => {
    const startM = hhmmToMinutes(normalizeHhmm(start) || start);
    return timeOptions.filter((o) => {
      const m = hhmmToMinutes(o.value);
      if (m == null) return false;
      if (startM != null && m <= startM) return false;
      return true;
    });
  }, [timeOptions, start]);

  const resetDraftFromSaved = useCallback(() => {
    if (accountWh.loading || !userInfo) return;
    if (userHasWorkingHoursOverride(userInfo)) {
      setStart(String(userInfo.working_hours_start).trim());
      setEnd(String(userInfo.working_hours_end).trim());
      setTz(
        userInfo.working_hours_time_zone != null
          ? String(userInfo.working_hours_time_zone).trim()
          : ""
      );
    } else {
      setStart(accountWh.workingHoursStart);
      setEnd(accountWh.workingHoursEnd);
      setTz(
        accountWh.accountTimeZone ? String(accountWh.accountTimeZone).trim() : ""
      );
    }
  }, [
    accountWh.loading,
    accountWh.workingHoursStart,
    accountWh.workingHoursEnd,
    accountWh.accountTimeZone,
    userInfo,
  ]);

  useEffect(() => {
    if (editing) return;
    resetDraftFromSaved();
  }, [
    editing,
    resetDraftFromSaved,
    userInfo?._id,
    userInfo?.working_hours_start,
    userInfo?.working_hours_end,
    userInfo?.working_hours_time_zone,
    accountWh.workingHoursStart,
    accountWh.workingHoursEnd,
    accountWh.accountTimeZone,
    accountWh.loading,
  ]);

  const savedOverride = userHasWorkingHoursOverride(userInfo);

  const viewStart = savedOverride
    ? String(userInfo.working_hours_start).trim()
    : accountWh.workingHoursStart;
  const viewEnd = savedOverride
    ? String(userInfo.working_hours_end).trim()
    : accountWh.workingHoursEnd;
  const viewTzRaw = savedOverride
    ? userInfo.working_hours_time_zone != null
      ? String(userInfo.working_hours_time_zone).trim()
      : ""
    : accountWh.accountTimeZone
      ? String(accountWh.accountTimeZone).trim()
      : "";

  const dirty =
    editing &&
    !draftMatchesPersisted(
      start,
      end,
      tz,
      userInfo,
      accountWh.workingHoursStart,
      accountWh.workingHoursEnd,
      accountWh.accountTimeZone || ""
    );

  const handleStartChange = (v) => {
    const nextStart = v || DEFAULT_START;
    setStart(nextStart);
    setEnd((prevEnd) => {
      const a = hhmmToMinutes(nextStart);
      const b = hhmmToMinutes(prevEnd);
      if (a == null || b == null) return prevEnd;
      if (b <= a) {
        const first = timeOptions.find((o) => {
          const m = hhmmToMinutes(o.value);
          return m != null && m > a;
        });
        return first ? first.value : prevEnd;
      }
      return prevEnd;
    });
  };

  const handleEndChange = (v) => {
    const nextEnd = v || DEFAULT_END;
    setEnd(nextEnd);
    setStart((prev) => {
      const b = hhmmToMinutes(nextEnd);
      const a = hhmmToMinutes(prev);
      if (a == null || b == null) return prev;
      if (a >= b) {
        const last = [...timeOptions].reverse().find((o) => {
          const m = hhmmToMinutes(o.value);
          return m != null && m < b && m <= maxStartMinutes;
        });
        return last ? last.value : prev;
      }
      return prev;
    });
  };

  useEffect(() => {
    if (!editing) return;
    const a = hhmmToMinutes(start);
    const b = hhmmToMinutes(end);
    if (a == null || b == null) return;
    if (b <= a) {
      const first = timeOptions.find((o) => {
        const m = hhmmToMinutes(o.value);
        return m != null && m > a;
      });
      if (first) setEnd(first.value);
    }
  }, [editing, start, end, timeOptions]);

  const beginEdit = () => {
    setError("");
    resetDraftFromSaved();
    setEditing(true);
  };

  const handleCancel = () => {
    setError("");
    resetDraftFromSaved();
    setEditing(false);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setError("");
    if (!user || accountWh.loading || !editing) return;

    const orgS = accountWh.workingHoursStart;
    const orgE = accountWh.workingHoursEnd;
    const accTz = accountWh.accountTimeZone || "";

    const a = hhmmToMinutes(start);
    const b = hhmmToMinutes(end);
    if (a == null || b == null) {
      setError("Choose valid start and end times.");
      return;
    }
    if (b <= a) {
      setError("End time must be after start time.");
      return;
    }

    if (!dirty) return;

    setSaving(true);
    try {
      const orgDefault = isOrgDefaultDraft(start, end, tz, orgS, orgE, accTz);
      if (orgDefault) {
        await updateMyWorkingHours({ use_organization_default: true });
      } else {
        await updateMyWorkingHours({
          working_hours_start: normalizeHhmm(start) || String(start).trim(),
          working_hours_end: normalizeHhmm(end) || String(end).trim(),
          working_hours_time_zone: (tz || "").trim() || null,
        });
      }
      const next = await exchangeFirebaseTokenForUserInfo(user);
      setUserInfo(next);
      setEditing(false);
    } catch (err) {
      setError(getMessageForFetchError(err) || err?.message || "Could not save.");
    } finally {
      setSaving(false);
    }
  };

  const readHint = savedOverride
    ? "Your schedule · click to edit"
    : "Organization default · click to edit";

  return (
    <div style={{ marginTop: 0, paddingTop: 0, borderTop: "none" }}>
      {!editing ? (
        <button
          type="button"
          onClick={beginEdit}
          className="input"
          disabled={accountWh.loading || !user}
          style={{
            width: "100%",
            textAlign: "left",
            cursor: "pointer",
            background: "#f9fafb",
            border: "1px solid #e5e7eb",
            borderRadius: "8px",
            padding: "12px 14px",
            fontSize: "14px",
            color: "#111827",
          }}
        >
          {accountWh.loading ? (
            <div style={{ fontWeight: 600 }}>Loading…</div>
          ) : (
            <>
              <div style={{ fontWeight: 600 }}>{formatRangePretty(viewStart, viewEnd)}</div>
              <div
                style={{
                  fontSize: "13px",
                  color: "#4b5563",
                  marginTop: "6px",
                  lineHeight: 1.35,
                  wordBreak: "break-word",
                }}
              >
                {timezoneDisplayLabel(viewTzRaw, "user")}
              </div>
              <div style={{ fontSize: "12px", color: "#6b7280", marginTop: "6px" }}>{readHint}</div>
            </>
          )}
        </button>
      ) : (
        <form onSubmit={handleSave}>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "10px",
              marginBottom: "12px",
              minWidth: 0,
            }}
          >
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                alignItems: "center",
                gap: "8px",
              }}
            >
              <TimeSelect
                id={`${fieldIdPrefix}-start`}
                value={normalizeHhmm(start) || start}
                options={startOptions}
                onChange={handleStartChange}
                maxVisibleItems={5}
                title="Working hours start"
                style={timeSelectStyle}
                disabled={saving || accountWh.loading}
                dropdownPortal
              />
              <span style={{ color: "#6b7280", flexShrink: 0, fontSize: "13px" }}>to</span>
              <TimeSelect
                id={`${fieldIdPrefix}-end`}
                value={normalizeHhmm(end) || end}
                options={endOptions}
                onChange={handleEndChange}
                maxVisibleItems={5}
                title="Working hours end"
                style={timeSelectStyle}
                disabled={saving || accountWh.loading}
                dropdownPortal
              />
            </div>
            <TimezoneSelect
              variant="user"
              id={`${fieldIdPrefix}-tz`}
              value={tz}
              onChange={setTz}
              disabled={saving || accountWh.loading}
            />
          </div>

          {error ? (
            <div
              style={{ color: "#b91c1c", fontSize: "13px", marginBottom: "10px" }}
              role="alert"
            >
              {error}
            </div>
          ) : null}

          <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
            <button
              type="submit"
              className="btn btn-primary btn-sm"
              disabled={!dirty || saving || !user || accountWh.loading}
            >
              {saving ? "Saving…" : "Save"}
            </button>
            <button
              type="button"
              className="btn btn-outline-secondary btn-sm"
              onClick={handleCancel}
              disabled={saving}
            >
              Cancel
            </button>
          </div>
        </form>
      )}
    </div>
  );
}

export default ProfileWorkingHoursPanel;
